import { Trans, useLingui } from '@lingui/react/macro'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'designSystem/components/Select'
import {
  type CanonicalEndpoint,
  resolvePairDetail,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import type { FimRevisionDiffDetailEntry } from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import type { PageRevisionFamilyMember } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import styled from 'styled-components'

const Root = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
`

const PickerField = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
`

const FieldLabel = styled.span`
  color: var(--muted-foreground);
  font-size: 0.75rem;
`

type RevisionPairPickerProps = {
  /** One member per revision, latest-first. */
  members: readonly PageRevisionFamilyMember[]
  fromDocumentId: string | undefined
  toDocumentId: string | undefined
  details: readonly FimRevisionDiffDetailEntry[]
  canonicalEndpoint: CanonicalEndpoint
  onSelectPair: (
    from: PageRevisionFamilyMember,
    to: PageRevisionFamilyMember
  ) => void
}

export function RevisionPairPicker({
  members,
  fromDocumentId,
  toDocumentId,
  details,
  canonicalEndpoint,
  onSelectPair,
}: RevisionPairPickerProps) {
  const { t } = useLingui()

  if (members.length < 2) return null

  const unknownRevisionLabel = t`unknown`
  const indexById = new Map<string, number>()
  const memberById = new Map<string, PageRevisionFamilyMember>()
  members.forEach((member, index) => {
    if (!indexById.has(member.bqDocumentId)) {
      indexById.set(member.bqDocumentId, index)
      memberById.set(member.bqDocumentId, member)
    }
  })
  const options = members.filter(
    (member, index) => indexById.get(member.bqDocumentId) === index
  )

  const optionLabel = (member: PageRevisionFamilyMember) => {
    const revisionLabel = member.revisionCode || unknownRevisionLabel
    return [t`Rev ${revisionLabel}`, member.revisionDate]
      .filter(Boolean)
      .join(' · ')
  }

  const isPairAvailable = (fromId: string, toId: string) => {
    const fromIndex = indexById.get(fromId)
    const toIndex = indexById.get(toId)
    if (fromIndex === undefined || toIndex === undefined) return false
    if (toIndex >= fromIndex) return false
    const fromMember = memberById.get(fromId)
    const toMember = memberById.get(toId)
    if (!fromMember || !toMember) return false
    return (
      resolvePairDetail(details, fromMember, toMember, canonicalEndpoint) !==
      undefined
    )
  }

  const enabledFrom = (id: string) =>
    options.some((option) => isPairAvailable(id, option.bqDocumentId))
  const enabledTo = (id: string) =>
    options.some((option) => isPairAvailable(option.bqDocumentId, id))

  const handleFromChange = (next: string) => {
    if (!toDocumentId) return
    const nextMember = memberById.get(next)
    const toMember = memberById.get(toDocumentId)
    if (nextMember && toMember && isPairAvailable(next, toDocumentId)) {
      onSelectPair(nextMember, toMember)
      return
    }
    const nextIndex = indexById.get(next)
    if (nextIndex === undefined) return
    const partner = options
      .filter(
        (option) =>
          (indexById.get(option.bqDocumentId) ?? Infinity) < nextIndex &&
          isPairAvailable(next, option.bqDocumentId)
      )
      .at(-1)
    if (nextMember && partner) onSelectPair(nextMember, partner)
  }

  const handleToChange = (next: string) => {
    if (!fromDocumentId) return
    const nextMember = memberById.get(next)
    const fromMember = memberById.get(fromDocumentId)
    if (nextMember && fromMember && isPairAvailable(fromDocumentId, next)) {
      onSelectPair(fromMember, nextMember)
      return
    }
    const nextIndex = indexById.get(next)
    if (nextIndex === undefined) return
    const partner = options.find(
      (option) =>
        (indexById.get(option.bqDocumentId) ?? -Infinity) > nextIndex &&
        isPairAvailable(option.bqDocumentId, next)
    )
    if (nextMember && partner) onSelectPair(partner, nextMember)
  }

  return (
    <Root>
      <PickerField>
        <FieldLabel>
          <Trans>From</Trans>
        </FieldLabel>
        <Select value={fromDocumentId} onValueChange={handleFromChange}>
          <SelectTrigger size='sm' aria-label={t`From revision`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((member) => (
              <SelectItem
                key={member.bqDocumentId}
                value={member.bqDocumentId}
                disabled={!toDocumentId || !enabledFrom(member.bqDocumentId)}>
                {optionLabel(member)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PickerField>
      <PickerField>
        <FieldLabel>
          <Trans>To</Trans>
        </FieldLabel>
        <Select value={toDocumentId} onValueChange={handleToChange}>
          <SelectTrigger size='sm' aria-label={t`To revision`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((member) => (
              <SelectItem
                key={member.bqDocumentId}
                value={member.bqDocumentId}
                disabled={!fromDocumentId || !enabledTo(member.bqDocumentId)}>
                {optionLabel(member)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PickerField>
    </Root>
  )
}
